/**
 * Organization Context for AdCanvas
 *
 * Provides organization state and settings throughout the app.
 * Manages the current organization for multi-tenant functionality.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase, getAuthHeaders } from '../utils/supabase/client';
import { EDGE_FUNCTIONS_BASE } from '../utils/edge';
import { useAuth } from './AuthContext';

const FUNCTIONS_URL = EDGE_FUNCTIONS_BASE;

// ============================================================================
// TYPES
// ============================================================================

interface OrganizationSettings {
  facebook_account_id?: string;
  facebook_user_id?: string;
  google_account_id?: string;
  google_user_id?: string;
  default_date_range?: string;
  timezone?: string;
  has_custom_reports?: boolean;
}

interface Organization {
  id: string;
  name: string;
  slug: string;
  settings: OrganizationSettings;
  plan_type: string;
  created_at: string;
}

interface OrganizationMembership {
  organization_id: string;
  role: string;
  organization: Organization;
}

interface OrganizationContextType {
  currentOrganization: Organization | null;
  organizations: Organization[];
  membership: OrganizationMembership | null;
  isLoading: boolean;
  hasInitialLoad: boolean;
  error: string | null;
  setCurrentOrganization: (orgId: string) => void;
  refreshOrganizations: () => Promise<void>;
  updateOrganizationSettings: (settings: Partial<OrganizationSettings>) => Promise<{ error: Error | null }>;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const CURRENT_ORG_KEY = 'adcanvas_current_org';
const ORG_CACHE_KEY = 'adcanvas_org_cache';

// ============================================================================
// CACHE HELPERS
// ============================================================================

interface OrgCache {
  organizations: Organization[];
  currentOrgId: string | null;
  membership: OrganizationMembership | null;
  timestamp: number;
}

// Cache expires after 5 minutes (will refresh in background anyway)
const CACHE_TTL = 5 * 60 * 1000;

const getCachedOrgs = (): OrgCache | null => {
  try {
    const cached = localStorage.getItem(ORG_CACHE_KEY);
    if (cached) {
      const parsed: OrgCache = JSON.parse(cached);
      // Check if cache is still valid
      if (Date.now() - parsed.timestamp < CACHE_TTL) {
        return parsed;
      }
    }
  } catch {
    // Ignore errors
  }
  return null;
};

const setCachedOrgs = (orgs: Organization[], currentOrgId: string | null, membership: OrganizationMembership | null) => {
  try {
    const cache: OrgCache = {
      organizations: orgs,
      currentOrgId,
      membership,
      timestamp: Date.now(),
    };
    localStorage.setItem(ORG_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Ignore errors (e.g., quota exceeded)
  }
};

const clearOrgCache = () => {
  try {
    localStorage.removeItem(ORG_CACHE_KEY);
  } catch {
    // Ignore errors
  }
};

// ============================================================================
// CONTEXT
// ============================================================================

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface OrganizationProviderProps {
  children: React.ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const { user, isAuthenticated, isInitialized, isBatman, isBatmanResolved } = useAuth();

  // SIMPLIFIED: Don't use cache for initial state - prevents race conditions
  // With single tenant, the ~200ms fetch is acceptable
  const [currentOrganization, setCurrentOrganizationState] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [membership, setMembership] = useState<OrganizationMembership | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user's organizations
  const fetchOrganizations = useCallback(async (isRefresh = false) => {
    if (!user) {
      setOrganizations([]);
      setCurrentOrganizationState(null);
      setMembership(null);
      setIsLoading(false);
      setHasInitialLoad(true);
      return;
    }

    // Show loading only on initial fetch, not manual refreshes
    if (!isRefresh) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Batman can access ALL organizations
      if (isBatman) {
        const { data: allOrgs, error: fetchError } = await supabase
          .from('organizations')
          .select('id, name, slug, settings, plan_type, created_at')
          .order('name');

        if (fetchError) {
          console.error('Error fetching all organizations for Batman:', fetchError);
          setError(fetchError.message);
          setIsLoading(false);
          setHasInitialLoad(true);
          return;
        }

        setOrganizations(allOrgs || []);

        // Set current org from localStorage or first available
        const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY);
        const savedOrg = allOrgs?.find((o) => o.id === savedOrgId);
        const currentOrg = savedOrg || allOrgs?.[0] || null;

        setCurrentOrganizationState(currentOrg);
        if (currentOrg) {
          localStorage.setItem(CURRENT_ORG_KEY, currentOrg.id);
          // Batman has effective owner access to all orgs
          setMembership({
            organization_id: currentOrg.id,
            role: 'owner',
            organization: currentOrg,
          });
        }

        setIsLoading(false);
        setHasInitialLoad(true);
        return;
      }

      // PARALLEL: Run user upsert and membership fetch at the same time
      const [upsertResult, membershipResult] = await Promise.all([
        // Ensure public.users record exists (safeguard for invited users)
        supabase.from('users').upsert({
          id: user.id,
          email: user.email,
          name: user.user_metadata?.name || user.email?.split('@')[0] || 'User',
        }, { onConflict: 'id' }),
        // Fetch memberships with organization data
        supabase
          .from('organization_members')
          .select(`
            organization_id,
            role,
            status,
            organizations (
              id,
              name,
              slug,
              settings,
              plan_type,
              created_at
            )
          `)
          .eq('user_id', user.id)
          .eq('status', 'active'),
      ]);

      if (upsertResult.error) {
        console.warn('Could not ensure public.users record:', upsertResult.error.message);
      }

      const { data: memberships, error: fetchError } = membershipResult;

      if (fetchError) {
        console.error('Error fetching organizations:', fetchError);
        setError(fetchError.message);
        setIsLoading(false);
        setHasInitialLoad(true);
        return;
      }

      if (!memberships || memberships.length === 0) {
        // No memberships found - check for pending invitations (BLOCKING - must complete before setting hasInitialLoad)
        console.log('No organizations found, checking for pending invitations...');

        try {
          const headers = await getAuthHeaders();
          const response = await fetch(`${FUNCTIONS_URL}/team-members`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ action: 'accept-invitations', email: user.email }),
          });

          const data = await response.json();
          console.log('Accept invitations response:', data);

          if (data.accepted > 0) {
            console.log(`Accepted ${data.accepted} invitation(s), fetching organizations...`);

            // Fetch organizations again after accepting invitations
            const { data: newMemberships } = await supabase
              .from('organization_members')
              .select(`
                organization_id,
                role,
                status,
                organizations (
                  id,
                  name,
                  slug,
                  settings,
                  plan_type,
                  created_at
                )
              `)
              .eq('user_id', user.id)
              .eq('status', 'active');

            if (newMemberships && newMemberships.length > 0) {
              const orgs = newMemberships
                .map((m: any) => m.organizations as Organization)
                .filter(Boolean);

              setOrganizations(orgs);
              setCurrentOrganizationState(orgs[0]);
              localStorage.setItem(CURRENT_ORG_KEY, orgs[0].id);
              setMembership({
                organization_id: newMemberships[0].organization_id,
                role: newMemberships[0].role,
                organization: orgs[0],
              });
              // Success - hasInitialLoad will be set in finally block
            } else {
              // Invitations accepted but still no memberships (edge case)
              console.error('Invitations accepted but no memberships found.');
              setError('No organization access. Please contact your administrator.');
            }
          } else {
            // No pending invitations and no memberships
            console.error('User has no organization access. They must be invited to join an organization.');
            setError('No organization access. Please contact your administrator.');
          }
        } catch (err) {
          console.error('Error accepting invitations:', err);
          setError('No organization access. Please contact your administrator.');
        }
        // Return here - finally block will still run and set hasInitialLoad
        return;
      }

      // Extract organizations from memberships
      const orgs = memberships
        .map((m: any) => m.organizations as Organization)
        .filter(Boolean);

      setOrganizations(orgs);

      // Set current organization
      const savedOrgId = localStorage.getItem(CURRENT_ORG_KEY);
      const savedOrg = orgs.find((o: Organization) => o.id === savedOrgId);

      if (savedOrg) {
        setCurrentOrganizationState(savedOrg);
        const savedMembership = memberships.find((m: any) => m.organization_id === savedOrg.id);
        setMembership(savedMembership ? {
          organization_id: savedMembership.organization_id,
          role: savedMembership.role,
          organization: savedOrg,
        } : null);
      } else if (orgs.length > 0) {
        // Default to first organization
        setCurrentOrganizationState(orgs[0]);
        localStorage.setItem(CURRENT_ORG_KEY, orgs[0].id);
        const firstMembership = memberships[0];
        setMembership({
          organization_id: firstMembership.organization_id,
          role: firstMembership.role,
          organization: orgs[0],
        });
      }
    } catch (err) {
      console.error('Error in fetchOrganizations:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsLoading(false);
      setHasInitialLoad(true);
    }
  }, [user, isBatman]);

  // Fetch organizations when user changes
  useEffect(() => {
    console.log('[OrgContext] useEffect triggered:', { isInitialized, isAuthenticated, isBatmanResolved });
    // Wait for auth to be initialized before making any decisions
    if (!isInitialized) {
      console.log('[OrgContext] Waiting for auth to initialize...');
      return;
    }

    // CRITICAL: Wait for Batman status to be resolved before fetching
    // This prevents race condition where we fetch with isBatman=false and
    // set hasInitialLoad=true before the Batman query completes
    if (!isBatmanResolved) {
      console.log('[OrgContext] Waiting for Batman status to resolve...');
      return;
    }

    if (isAuthenticated) {
      console.log('[OrgContext] User authenticated, fetching orgs...');
      fetchOrganizations();
    } else {
      console.log('[OrgContext] User not authenticated, clearing state');
      // User logged out - clear cache
      setOrganizations([]);
      setCurrentOrganizationState(null);
      setMembership(null);
      setIsLoading(false);
      clearOrgCache();
    }
  }, [isAuthenticated, isInitialized, isBatmanResolved, fetchOrganizations]);

  // Sync org data to cache whenever it changes
  useEffect(() => {
    if (organizations.length > 0 && currentOrganization) {
      setCachedOrgs(organizations, currentOrganization.id, membership);
    }
  }, [organizations, currentOrganization, membership]);

  // Set current organization
  const setCurrentOrganization = useCallback((orgId: string) => {
    const org = organizations.find((o) => o.id === orgId);
    if (org) {
      console.log('[OrgContext] Switching to org:', org.name);
      setCurrentOrganizationState(org);
      localStorage.setItem(CURRENT_ORG_KEY, orgId);
      // Update membership for Batman (always owner access)
      if (isBatman) {
        setMembership({
          organization_id: org.id,
          role: 'owner',
          organization: org,
        });
      }
    }
  }, [organizations, isBatman]);

  // Refresh organizations (isRefresh=true to avoid showing loading state)
  const refreshOrganizations = useCallback(async () => {
    await fetchOrganizations(true);
  }, [fetchOrganizations]);

  // Update organization settings
  const updateOrganizationSettings = useCallback(async (
    settings: Partial<OrganizationSettings>
  ): Promise<{ error: Error | null }> => {
    if (!currentOrganization) {
      return { error: new Error('No organization selected') };
    }

    try {
      const newSettings = {
        ...currentOrganization.settings,
        ...settings,
      };

      const { error: updateError } = await supabase
        .from('organizations')
        .update({ settings: newSettings })
        .eq('id', currentOrganization.id);

      if (updateError) {
        return { error: new Error(updateError.message) };
      }

      // Update local state
      setCurrentOrganizationState({
        ...currentOrganization,
        settings: newSettings,
      });

      // Refresh to get latest data
      await refreshOrganizations();

      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error('Unknown error') };
    }
  }, [currentOrganization, refreshOrganizations]);

  const value: OrganizationContextType = {
    currentOrganization,
    organizations,
    membership,
    isLoading,
    hasInitialLoad,
    error,
    setCurrentOrganization,
    refreshOrganizations,
    updateOrganizationSettings,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}

export default OrganizationContext;
